import i18n from '@/utils/i18n';
import {
  Drawer,
  Icons,
  ScrollView,
  useReducedMotion,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import {
  ExperienceSwitcherProduct,
  useExperienceSwitcherProductsLoader,
} from '@experiences/containers/switch-experience-drawer/blocs/loaders/use-experience-switcher-products-loader';
import { MobileDrawerExperienceSwitch } from '@/experiences/sales-portal/containers/mobile-navigation-drawer/components/mobile-drawer-experience-switch';
import { MobileDrawerHeader } from '@/experiences/sales-portal/containers/mobile-navigation-drawer/components/mobile-drawer-header';
import { MobileDrawerNavItem } from '@/experiences/sales-portal/containers/mobile-navigation-drawer/components/mobile-drawer-nav-item';
import { MobileDrawerProfileFooter } from '@/experiences/sales-portal/containers/mobile-navigation-drawer/components/mobile-drawer-profile-footer';
import { partnerSupplier } from '@/utils/environment';
import { SidenavSingleItem } from '@/domain/navigation/types';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';
import { useWindowDimensions } from 'react-native';

const DRAWER_MAX_WIDTH = 324;
const DRAWER_WIDTH_RATIO = 0.8;
const REDUCED_MOTION_DRAWER_ANIMATION = '75ms';
const STANDARD_DRAWER_ANIMATION = 'takeover';
const FOOTER_PADDING_VERTICAL = 14;

interface MobileNavigationDrawerProps {
  employeeName: string | null;
  employeeUUID: string | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  overflowItems: SidenavSingleItem[];
}

/**
 * Overflow nav drawer for the mobile tab bar.
 *
 * Note: Always rendered through a Portal, and as such will never
 * have acess to the Navigation Stack (e.g. `useNavigation`/`useRoute`).
 * Instead, is reliant on the provided `overflowItems` owning their
 * own navigation through an `onPress` callback.
 */
export const MobileNavigationDrawer: React.FC<MobileNavigationDrawerProps> = ({
  employeeName,
  employeeUUID,
  onOpenChange,
  open,
  overflowItems,
}) => {
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const theme = useTheme();
  const reduceMotion = useReducedMotion();
  const drawerWidth = Math.min(
    DRAWER_MAX_WIDTH,
    windowWidth * DRAWER_WIDTH_RATIO,
  );
  const { mode, switchToExperience } = useExperienceStore();
  const { products } = useExperienceSwitcherProductsLoader();

  const storefront: ExperienceSwitcherProduct = {
    icon: Icons.Store,
    label: i18n.t('sales_portal.sidebar.storefront'),
    mobileEnabled: true,
    mode: ExperienceMode.OPERATOR_PORTAL,
  };
  const experiences = [...products, storefront].filter(
    (experience) => experience.mobileEnabled && experience.mode !== mode,
  );

  const close = () => onOpenChange(false);

  const handleSwitchToExperience = (experienceMode: ExperienceMode) => {
    close();
    switchToExperience(experienceMode);
  };

  return (
    <Drawer onOpenChange={onOpenChange} open={open}>
      <Drawer.Overlay />
      <Drawer.Content
        animation={
          reduceMotion
            ? REDUCED_MOTION_DRAWER_ANIMATION
            : STANDARD_DRAWER_ANIMATION
        }
        paddingVertical={0}
        width={drawerWidth}
      >
        <YStack
          flex={1}
          paddingBottom={insets.bottom}
          paddingTop={insets.top + theme.sk.spacing.spaceMedium}
          width="100%"
        >
          <MobileDrawerHeader
            logoUrl={theme.images.welcomeLogoUrl}
            supplierName={partnerSupplier.fullName}
          />
          <YStack flexShrink={0} paddingBottom={theme.sk.spacing.spaceSmall}>
            {experiences.map((experience) => (
              <MobileDrawerExperienceSwitch
                icon={experience.icon}
                key={experience.mode}
                label={experience.label}
                onPress={() => handleSwitchToExperience(experience.mode)}
              />
            ))}
          </YStack>
          <ScrollView flex={1}>
            {overflowItems.map((item) => (
              <MobileDrawerNavItem
                active={item.active}
                icon={item.icon}
                iconUrl={item.iconUrl}
                key={item.id ?? item.label}
                label={item.label}
                onPress={() => {
                  item.onPress();
                  close();
                }}
              />
            ))}
          </ScrollView>
          {employeeName && employeeUUID ? (
            <Drawer.Footer
              borderTopColor="$borderPrimary"
              paddingHorizontal="$16"
              paddingVertical={FOOTER_PADDING_VERTICAL}
            >
              <MobileDrawerProfileFooter
                name={employeeName}
                uuid={employeeUUID}
              />
            </Drawer.Footer>
          ) : null}
        </YStack>
      </Drawer.Content>
    </Drawer>
  );
};
